import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { useNavigate } from 'react-router-dom';
import { getDocumentAccessLogs, GetDocumentAccessLogsOutputType } from 'zite-endpoints-sdk';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { FileSearch, Download, ShieldAlert } from 'lucide-react';
import { isOwnerOrAdmin } from '@/lib/roles';
import { format, parseISO } from 'date-fns';

type LogEntry = GetDocumentAccessLogsOutputType['logs'][0];

export default function DocumentAccessLog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!user) return;
    if (!isOwnerOrAdmin(user.role)) { navigate('/'); return; }
    getDocumentAccessLogs({ limit: 200 }).then(d => setLogs(d.logs)).catch(() => {}).finally(() => setLoading(false));
  }, [user, navigate]);

  const filtered = logs.filter(l => {
    if (!search) return true;
    const s = search.toLowerCase();
    return l.documentTitle.toLowerCase().includes(s) || l.actorEmail.toLowerCase().includes(s) || l.documentCategory.toLowerCase().includes(s) || l.actorRole.toLowerCase().includes(s);
  });

  const exportCsv = () => {
    const headers = ['Timestamp', 'Action', 'Document', 'Category', 'Actor', 'Role', 'Owner', 'Page', 'Sensitive'];
    const rows = filtered.map(l => [l.timestamp, l.action, l.documentTitle, l.documentCategory, l.actorEmail, l.actorRole, l.documentOwnerEmail, l.accessPage, l.isSensitive ? 'Yes' : 'No']);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'document-access-log.csv'; a.click();
  };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>;

  const sensitiveCount = filtered.filter(l => l.isSensitive).length;

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <FileSearch className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-xl font-bold">Document Access Log</h1>
            <p className="text-sm text-muted-foreground">Privacy audit trail for document access</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv}><Download className="w-4 h-4 mr-1.5" />Export CSV</Button>
      </div>

      {sensitiveCount > 0 && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="py-3 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-500 shrink-0" />
            <p className="text-sm"><strong>{sensitiveCount}</strong> sensitive document access{sensitiveCount > 1 ? 'es' : ''} in the current view</p>
          </CardContent>
        </Card>
      )}

      <Input placeholder="Search by document, user, category, or role..." value={search} onChange={e => setSearch(e.target.value)} className="max-w-md" />

      {filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No access logs found</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(l => (
            <Card key={l.id} className={l.isSensitive ? 'border-amber-500/30' : ''}>
              <CardContent className="py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium">{l.documentTitle}</p>
                      <Badge variant={l.action === 'Downloaded' ? 'default' : 'secondary'} className="text-xs">{l.action}</Badge>
                      {l.isSensitive && <Badge variant="destructive" className="text-xs">Sensitive</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {l.actorEmail} ({l.actorRole}) · {l.documentCategory}
                      {l.documentOwnerEmail && l.documentOwnerEmail !== l.actorEmail ? ` · Owner: ${l.documentOwnerEmail}` : ''}
                    </p>
                    {l.accessPage && <p className="text-xs text-muted-foreground">Page: {l.accessPage}</p>}
                  </div>
                  <p className="text-xs text-muted-foreground shrink-0">
                    {l.timestamp ? format(parseISO(l.timestamp), 'dd MMM HH:mm') : '—'}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Coverage notice */}
      <Card>
        <CardContent className="py-4">
          <p className="text-xs text-muted-foreground">
            <strong>Log Coverage:</strong> Document views and downloads from My Docs, Shared Documents, 
            and Admin document areas are tracked. Certificate downloads and compliance PDF downloads 
            are tracked. External link clicks are not tracked.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
